// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { GetBillTosApiParameter } from "@insite/client-framework/Services/CustomersService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillTosDataView } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTos from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTos";
import translate from "@insite/client-framework/Translate";
import { BillToModel } from "@insite/client-framework/Types/ApiModels";
import DynamicDropdown, { DynamicDropdownPresentationProps, OptionObject } from "@insite/mobius/DynamicDropdown";
import { Button, Typography } from "@insite/mobius/index";
import { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import AddressBook from "@rc/App/Components/ChangeCustomer/AddressBook";
import { xScreen } from "@rc/XDL/Utilities";
import debounce from "lodash/debounce";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    noShipToAndCantCreate: boolean;
    setParameter: (parameter: GetBillTosApiParameter) => void;
    parameter: GetBillTosApiParameter;
    billTo?: BillToModel;
    onSelect: (billTo: BillToModel) => void;
    extendedStyles?: ChangeCustomerBillToSelectorStyles;
    isLoading?: boolean;
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => ({
    billTosDataView: getBillTosDataView(state, props.parameter),
    customerSettings: getSettingsCollection(state).customerSettings,
});

const mapDispatchToProps = {
    loadBillTos,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ChangeCustomerBillToSelectorStyles {
    billToSelectorDynamicDropdown?: DynamicDropdownPresentationProps;

    // xng-change
    wrapper?: InjectableCss;
    title?: TypographyProps;
    billToActions?: InjectableCss;
}

export const changeCustomerBillToSelectorStyles: ChangeCustomerBillToSelectorStyles = {
    billToSelectorDynamicDropdown: {
        mergeCss: true,
        cssOverrides: {
            option: css`
                ${({ disabled }: any) =>
                    disabled
                        ? `
                        &&& {
                            opacity: 1;
                            font-weight: bold;
                            padding: 9px;
                            border-bottom: 1px solid #d7d7d7;
                            margin-bottom: 8px;
                        }
                `
                        : ""}
            `,
        },
    },
    wrapper: {
        css: css`
            width: 100%;
            input::placeholder {
                opacity: 1;
            }
        `,
    },
    title: {
        variant: "h6",
        css: css`
            margin-top: 10px;
            margin-bottom: 0;
        `,
    },
    billToActions: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            align-items: flex-start;
            justify-content: space-between;
            margin-bottom: 32px;
            p {
                min-width: 315px;
                max-width: 385px;
                margin-right: 20px;
                margin-top: 10px;
            }
            button {
                margin-top: 10px;
                ${xScreen("lte", "xs")} {
                    margin-top: 20px;
                }
            }
        `,
    },
};

const ChangeCustomerBillToSelector: FC<Props> = ({
    noShipToAndCantCreate,
    parameter,
    setParameter,
    billTo,
    onSelect,
    extendedStyles,
    isLoading,
    billTosDataView,
}) => {
    const [styles] = useState(() => mergeToNew(changeCustomerBillToSelectorStyles, extendedStyles));
    const [options, setOptions] = useState<OptionObject[]>([]);

    const [isModalOpen, setIsModalOpen] = useState(false);

    useEffect(() => {
        if (billTosDataView.value) {
            const options: OptionObject[] = billTosDataView.value.map(billTo => ({
                optionText: `${billTo.customerNumber ? `${billTo.customerNumber} ` : ""}`
                    + `${billTo.customerName ? `${billTo.customerName}` : ""}`
                    + `${billTo.address1 ? `, ${billTo.address1}` : ""}`
                    + `${billTo.state?.abbreviation ? `, ${billTo.state?.abbreviation}` : ""}`,
                optionValue: billTo.id,
            }));
            if (billTosDataView?.pagination?.totalItemCount && billTosDataView?.pagination?.totalItemCount > 20) {
                options.unshift({
                    optionText: translate(`Showing 1-15 of ${billTosDataView?.pagination?.totalItemCount}`),
                    optionValue: undefined,
                    disabled: true,
                });
            }
            setOptions(options);
        }
    }, [billTosDataView]);

    const searchTextChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        debouncedDoSearch(event.currentTarget.value);
    };

    const debouncedDoSearch = debounce((value: string) => {
        setParameter({
            ...parameter,
            filter: value,
        });
    }, 800);

    const selectCustomerHandler = (billToId?: string) => {
        if (billTosDataView.value && billToId) {
            const billTo = billTosDataView.value.filter(billTo => billTo.id === billToId)[0];
            if (billTo) {
                onSelect(billTo as BillToModel);
            }
        }
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography {...styles.title}>{translate("Search Customer Database")}</Typography>
            <StyledWrapper {...styles.billToActions}>
                <Typography as="p">{siteMessage("Custom_Select_BillTo_Message")}</Typography>
                <Button
                    variant="secondary"
                    sizeVariant="small"
                    onClick={() => {
                        setIsModalOpen(true);
                    }}
                >
                    {translate("Start Search")}
                </Button>
            </StyledWrapper>
            <Typography {...styles.title}>{translate("or Search by Billing Account #")}</Typography>
            <DynamicDropdown
                {...styles.billToSelectorDynamicDropdown}
                {...{ isSearchDropdown: true }}
                onSelectionChange={selectCustomerHandler}
                onInputChange={searchTextChanged}
                selected={billTo?.id}
                options={options}
                placeholder={translate("Enter Billing Account #")}
                isLoading={isLoading}
                error={noShipToAndCantCreate && siteMessage("SignIn_NoShipToAndCantCreate")}
                data-test-selector="changeCustomerBillToSelector"
                filterOption={() => true}
            />
            {isModalOpen && (
                <AddressBook
                    isShipTo={false}
                    onClose={() => {
                        setIsModalOpen(false);
                    }}
                    onRowSelect={(billTo: BillToModel) => {
                        onSelect(billTo as BillToModel);
                    }}
                />
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ChangeCustomerBillToSelector);
